<template>
  <ChartContainer ref="chartContainerRef" :title="title" :subtitle="subtitle" :ready="ready"
    :legend-items="currentLegendItems" :hidden-series="hiddenSeries"
    :series-data="chartType === 'pie' ? chartData : series" :toggle-series="toggleSeries" :pie-data="chartData"
    :show-legend="showLegend" :skeletonType="skeletonType" :show-total="showTotal" :stagger-index="staggerIndex">
    <template #default="slotProps">
      <div class="chart-content">
        <DisplayState v-if="emptyMessage" iconName="empty" :stateText="emptyMessage" stateSubText="" />
        <DisplayState v-else-if="error" iconName="error" stateText="Something went wrong" :stateSubText="errorMessage" />
        <component v-else :is="chartComponent" :key="chartType === 'pie' ? chartKey : undefined"
          v-bind="chartProps(slotProps)" />
      </div>
    </template>

  </ChartContainer>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

import ChartContainer from '@/components/Graph/ChartContainer.vue'
import LineChart from '@/components/Graph/LineChart.vue'
import BarChart from '@/components/Graph/BarChart.vue'
import PieChart from '@/components/Graph/PieChart.vue'
import DisplayState from '@/components/Automation/Table/DisplayState.vue'

import { useChartSeries } from '@/Modules/Grafer/useVisibleSeries'
import { populateGraphComponent } from '@/Modules/Grafer/common/populateGraph'
import { useChartColors } from '@/Modules/Grafer/useChartColors'
import { generateColors } from '@/Modules/utils/generateColors'

const props = defineProps({
  title: { type: String, required: true },
  subtitle: { type: String, required: true },
  chartType: { type: String, required: true, validator: v => ['line', 'bar', 'pie'].includes(v) },
  height: { type: Number, default: 360 },
  showTotal: { type: Boolean, default: true },
  dataProcessor: { type: Function, required: true },
  colorMap: { type: Object, default: () => ({}) },
  labelYAxis: { type: String, default: '' },
  labelYAxisSecondary: { type: String, default: '' },
  secondaryAxisSeries: { type: Array, default: () => [] },
  showLegend: { type: Boolean, default: true },
  horizontal: { type: Boolean, default: false },
  extractUniqueItems: { type: Function },
  categoryExtractor: { type: Function },
  staggerIndex: {
    type: Number,
    default: 0
  }
})

const chartContainerRef = ref(null)
const ready = ref(false)
const chartData = ref([])
const categories = ref([])
const isInView = ref(false)
const shouldBeReady = ref(false)
const hasBeenDisplayed = ref(false)
const chartKey = ref(0)
let intersectionObserver = null

const error = ref(false)
const errorMessage = ref('Failed to load chart data.')
const emptyMessage = ref('')

const {
  series,
  hiddenSeries,
  visibleSeries,
  legendItems: currentLegendItems,
  toggleSeries,
  setSeries,
  setPieData,
  pieColors
} = useChartSeries({
  colorMap: props.colorMap,
  chartType: props.chartType,
  extractUniqueItems: props.extractUniqueItems,
  generateColors
})

const computedColors = useChartColors(visibleSeries, currentLegendItems)

const updateReadyState = () => {
  if (error.value || emptyMessage.value) {
    ready.value = true;
    return;
  }

  if (hasBeenDisplayed.value) {
    ready.value = shouldBeReady.value;
    return;
  }

  const newReadyState = isInView.value && shouldBeReady.value;

  if (newReadyState && !ready.value) {
    hasBeenDisplayed.value = true;

    if (intersectionObserver) {
      intersectionObserver.disconnect();
      intersectionObserver = null;
    }
  }

  ready.value = newReadyState;
};

const skeletonType = computed(() => {
  return { line: 'lines', bar: 'bar', pie: 'pie' }[props.chartType]
})

const chartComponent = computed(() => {
  return { line: LineChart, bar: BarChart, pie: PieChart }[props.chartType]
})

const chartProps = computed(() => (slotProps) => {
  const base = { height: props.height }

  if (props.chartType === 'pie') {
    return {
      ...base,
      data: slotProps.data || chartData.value,
      colors: pieColors.value,
      'legend-items': currentLegendItems.value
    }
  }

  return {
    ...base,
    series: slotProps.series || visibleSeries.value,
    categories: categories.value,
    'legend-items': currentLegendItems.value,
    colors: computedColors.value,
    'label-y-axis': props.labelYAxis,
    'label-y-axis-secondary': props.labelYAxisSecondary,
    'secondary-axis-series': props.secondaryAxisSeries,
    horizontal: props.horizontal
  }
})

const processData = (newData) => {
  const result = props.dataProcessor(newData)

  if (props.chartType === 'pie') {
    chartData.value = Array.isArray(result) ? result : result?.data || []
  } else {
    if (result.series) setSeries(result.series)

    if (result.categories) {
      categories.value = result.categories
    } else if (result.xaxis?.categories) {
      categories.value = result.xaxis.categories
    } else if (props.categoryExtractor && result.data) {
      categories.value = result.data.map(props.categoryExtractor)
    } else if (result.length) {
      chartData.value = result
      if (props.categoryExtractor) {
        categories.value = result.map(props.categoryExtractor)
      }
    }
  }
}

const populateComponent = (newData, options = {}) => {
  error.value = false;
  emptyMessage.value = options.emptyMessage || '';

  if (emptyMessage.value) {
    shouldBeReady.value = true;
    updateReadyState();
    return;
  }

  if (!newData || (Array.isArray(newData) && newData.length === 0)) {
    error.value = true;
    errorMessage.value = 'No data available or failed to load.';
    shouldBeReady.value = true;
    updateReadyState();
    return;
  }

  try {
    if (props.chartType === 'pie') {
      setPieData(newData);
      const processed = props.dataProcessor(newData);
      chartData.value = Array.isArray(processed) ? processed : processed?.data || [];
      shouldBeReady.value = true;
      updateReadyState();
    } else {
      populateGraphComponent({
        newData,
        processor: processData,
        dataRef: chartData,
        readyRef: shouldBeReady
      });
      updateReadyState();
    }
  } catch (err) {
    console.log(props)
    console.log("error", err);
    error.value = true;
    errorMessage.value = err?.message || 'An unexpected error occurred.';
    shouldBeReady.value = true;
    updateReadyState();
  }
};
const setupIntersectionObserver = () => {
  if (!chartContainerRef.value) return

  intersectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        isInView.value = entry.isIntersecting
        updateReadyState()
      })
    },
    {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    }
  )
  intersectionObserver.observe(chartContainerRef.value.$el || chartContainerRef.value)
}

onMounted(() => {
  setupIntersectionObserver()
})

onUnmounted(() => {
  if (intersectionObserver) {
    intersectionObserver.disconnect()
  }
})

const setReady = (value) => {
  if (!value) {
    ready.value = false;
    error.value = false;
    emptyMessage.value = '';
    shouldBeReady.value = false;
    hasBeenDisplayed.value = false;
  } else {
    shouldBeReady.value = true;
    updateReadyState();
  }
};

defineExpose({
  populateComponent,
  setSeries: props.chartType !== 'pie' ? setSeries : undefined,
  toggleSeries: props.chartType !== 'pie' ? toggleSeries : undefined,
  setReady
})
</script>